import { useState } from 'react';
import { ChevronDown, Building2, Shield, Lock } from 'lucide-react';
import { Logo } from '@/components/Logo';

const faqs = [
  { q: "Who can create an account on Tenantra?", a: "Only landlords can create and maintain accounts on Tenantra. This ensures that all rental records and reports come from a consistent and accountable source." },
  { q: "Can tenants create accounts?", a: "No. Tenants do not create accounts to submit reports. They are onboarded through a landlord-initiated process and must approve participation." },
  { q: "How are tenants added to Tenantra?", a: "A landlord initiates onboarding by entering the tenant's details. The tenant receives a notification explaining the process and must approve before the tenancy is recorded." },
  { q: "Can a tenant be added without their consent?", a: "No. Tenant approval is required before any tenancy is created on Tenantra. Without consent, no record exists and no report can be submitted." },
  { q: "When can a landlord submit a report?", a: "A report can only be submitted after a tenancy has ended. Tenantra does not allow reporting during an active tenancy." },
  { q: 'What does "Verified Rental History" mean?', a: "It means the rental record was created through a verified landlord account, tenant-approved onboarding, and a recorded tenancy lifecycle. It does not mean Tenantra independently investigates every detail of the tenancy." },
  { q: "Can a landlord report any tenant at any time?", a: "No. A landlord can only report a tenant who was onboarded with consent and had a tenancy recorded on the platform." },
  { q: "What happens after a report is submitted?", a: "Once a report is submitted, it becomes part of the tenant's rental record on Tenantra. The tenant is then given the opportunity to review and dispute the report if anything is inaccurate or inconsistent." },
  { q: "Can tenants dispute a report?", a: "Yes. Tenants can dispute any report they believe is incorrect, misleading, or incomplete. This is a core part of the platform to ensure fairness and accountability." },
  { q: "How does the dispute process work?", a: "If a tenant raises a dispute, the report is flagged, the tenancy record and supporting information may be reviewed, and appropriate action may be taken based on the findings." },
  { q: "What prevents false reporting?", a: "All reports are tied to a verified landlord account and a consent-based tenancy record. Tenants also have the ability to dispute reports, which adds an additional layer of accountability." },
  { q: "Does Tenantra verify property ownership?", a: "Tenantra may collect supporting documents related to an address for internal records and review. However, it does not function as a government registry or independently verify property ownership." },
  { q: "Why are landlords asked to upload documents?", a: "Supporting documents help maintain an internal audit trail and may be used if a report is questioned or disputed." },
  { q: 'Is Tenantra deciding if a tenant is "good" or "bad"?', a: "No. Tenantra provides structured rental history information to help landlords make more informed decisions. It does not replace judgment or other screening processes." },
  { q: "Does Tenantra allow anonymous reporting?", a: "No. Reports can only be submitted by verified landlord accounts linked to a recorded tenancy." },
  { q: "Why is tenant consent required?", a: "Tenant consent ensures that rental relationships are recorded transparently and with awareness from both parties." },
  { q: "Does Tenantra replace credit checks or background checks?", a: "No. Tenantra is designed to complement existing screening tools, not replace them." },
  { q: "Who is responsible for the information submitted?", a: "Landlords are responsible for the reports they submit. All activity is tied to their verified account." }
];

export default function FAQPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#FAF8F5]/95 backdrop-blur-md border-b border-stone-300/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo size="md" />
          <a href="/" className="text-sm text-stone-600 hover:text-stone-900 transition-colors">
            Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8 sm:mb-10">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-stone-900 mb-2">Frequently Asked Questions</h1>
          <p className="text-sm sm:text-base text-stone-500">Everything you need to know about how Tenantra works.</p>
        </div>

        <div className="border border-stone-200 rounded-xl bg-white overflow-hidden divide-y divide-stone-200">
          {faqs.map((faq, i) => (
            <div key={i}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-stone-50 transition-colors"
                data-testid={`faq-item-${i}`}
              >
                <span className="text-sm font-medium text-stone-800 pr-4">{faq.q}</span>
                <ChevronDown className={`w-4 h-4 text-stone-400 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ease-out ${openFaq === i ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                <p className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-stone-300 bg-[#FAF8F5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-stone-500">&copy; {new Date().getFullYear()} Tenantra Inc. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-xs text-stone-500">
              <Lock className="w-3.5 h-3.5" /> 256-bit Encryption
            </span>
            <span className="flex items-center gap-1.5 text-xs text-stone-500">
              <Shield className="w-3.5 h-3.5" /> PIPEDA Compliant
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
